/**
 * Orthogonal capability axes for a HAST node.
 *
 * Three independent axes rather than a zone enum, because an ordinary anchor
 * needs all three to disagree: its label may be typeset transactionally, while
 * it stays opaque to block-marker discovery and keeps an interactive contract.
 * Both the HAST plugin and the React table adapters read this one table, so the
 * two surfaces cannot drift apart.
 */
import { parseLink } from "../markdown-actions";
import { isEditorialGenerated } from "./editorial-provenance";

export type EditorialInlineDisplay =
    | "transform"
    | "transform-transactional"
    | "preserve"
    | "adapter-only";

export type EditorialBlockScan = "target" | "transparent" | "opaque";

export type EditorialContract =
    | "none"
    | "interactive"
    | "protocol"
    | "verbatim"
    | "source-model"
    | "generated";

export interface EditorialCapabilities {
    readonly inlineDisplay: EditorialInlineDisplay;
    readonly blockScan: EditorialBlockScan;
    readonly contract: EditorialContract;
}

/**
 * Whether what is inside a node is this layer's to read and rewrite.
 *
 * Named here rather than spelled at each pass because every pass was asking
 * the same question of the same classifier, and they had drifted into two
 * phrasings: an allow-list of the two touchable contracts, and a deny-list of
 * the other four. Equivalent over today's union — and they fail in OPPOSITE
 * directions the moment a seventh contract is added, the deny-list sites
 * admitting it by omission.
 *
 * Stated as the allowed set, so a new contract is off-limits until someone
 * decides otherwise. `interactive` is touchable because an anchor's label is
 * ordinary prose; refusing to empty that label is a separate rule, enforced
 * where the emptying is decided.
 */
const TOUCHABLE_CONTRACTS: ReadonlySet<EditorialContract> = new Set([
    "none",
    "interactive",
]);

export const isEditorialTouchable = (contract: EditorialContract): boolean =>
    TOUCHABLE_CONTRACTS.has(contract);

/**
 * The one node shape the editorial layer passes around, exported so the
 * modules downstream do not each declare their own — independent copies can
 * disagree on `children`'s mutability and drift apart silently.
 *
 * `children` is mutable because edit plans splice it. Everything is optional
 * because these nodes arrive from hast, mdast and hand-built fixtures alike.
 */
export interface EditorialHastNode {
    readonly type?: string;
    readonly tagName?: string;
    readonly value?: string;
    readonly properties?: Record<string, unknown>;
    children?: EditorialHastNode[];
}

type HastNodeLike = EditorialHastNode;

const VERBATIM_TAGS: ReadonlySet<string> = new Set([
    "code",
    "pre",
    "blockquote",
]);

const TABLE_TAGS: ReadonlySet<string> = new Set([
    "table",
    "thead",
    "tbody",
    "tfoot",
    "tr",
    "th",
    "td",
    "caption",
    "colgroup",
    "col",
]);

// hast normalizes `data-status` to `dataStatus`, but a hand-built or
// raw-parsed node can carry either spelling; checking both keeps a tagged
// span from being mistaken for ordinary prose.
const TAGGED_SPAN_PROPERTIES = [
    "dataStatus",
    "data-status",
    "dataBool",
    "data-bool",
];

const asNode = (value: unknown): HastNodeLike | undefined =>
    value !== null && typeof value === "object"
        ? (value as HastNodeLike)
        : undefined;

const hasTaggedSpanProperty = (node: HastNodeLike): boolean => {
    const properties = node.properties;
    if (!properties) return false;
    return TAGGED_SPAN_PROPERTIES.some((name) => name in properties);
};

/**
 * The single protocol detector. Href schemes defer to `parseLink`, which the
 * `<a>` override and the DOM table parser already share — a second scheme
 * matcher here is the drift bug this consolidation exists to prevent.
 */
export const isProtocolNode = (value: unknown): boolean => {
    const node = asNode(value);
    if (!node || node.type !== "element") return false;

    if (node.tagName === "a") {
        const href = node.properties?.href;
        return typeof href === "string" && parseLink(href) !== null;
    }
    return node.tagName === "span" && hasTaggedSpanProperty(node);
};

const PROSE_TEXT: EditorialCapabilities = {
    inlineDisplay: "transform",
    blockScan: "target",
    contract: "none",
};

const TRANSPARENT: EditorialCapabilities = {
    inlineDisplay: "transform",
    blockScan: "transparent",
    contract: "none",
};

const OPAQUE: Record<
    "interactive" | "protocol" | "verbatim" | "generated",
    EditorialCapabilities
> = {
    interactive: {
        inlineDisplay: "transform-transactional",
        blockScan: "opaque",
        contract: "interactive",
    },
    protocol: {
        inlineDisplay: "preserve",
        blockScan: "opaque",
        contract: "protocol",
    },
    verbatim: {
        inlineDisplay: "preserve",
        blockScan: "opaque",
        contract: "verbatim",
    },
    generated: {
        inlineDisplay: "preserve",
        blockScan: "opaque",
        contract: "generated",
    },
};

const SOURCE_MODEL: EditorialCapabilities = {
    inlineDisplay: "adapter-only",
    blockScan: "opaque",
    contract: "source-model",
};

export const classifyEditorialCapabilities = (
    value: unknown
): EditorialCapabilities => {
    // Provenance is checked first and is the ONLY route to `generated`: a
    // `data-editorial-*` attribute is authorable from untrusted source and
    // proves nothing.
    if (isEditorialGenerated(value)) return OPAQUE.generated;

    const node = asNode(value);
    if (!node) return PROSE_TEXT;
    if (node.type === "text") return PROSE_TEXT;
    if (node.type !== "element") return TRANSPARENT;

    const tagName = node.tagName ?? "";
    if (VERBATIM_TAGS.has(tagName)) return OPAQUE.verbatim;
    if (TABLE_TAGS.has(tagName)) return SOURCE_MODEL;
    if (isProtocolNode(node)) return OPAQUE.protocol;
    if (tagName === "a") return OPAQUE.interactive;

    return TRANSPARENT;
};
